import { useMemo, useState } from 'react';
import { ApiError, getErrorMessage } from '../api/client';
import { recordWaste } from '../api/waste';
import type { CreateWasteInput, WasteCaptureItem, WasteCaptureOptions } from '../types/waste';
import { randomUuid } from '../utils/randomUuid';
import {
  asksWasteStore, buildWastePayload, emptyWasteDraft, isFinalWasteRejection, nextWasteStep, parseWasteQuantity, previousWasteStep, stepWasteQuantity,
  wasteCategoryRequiresNote, wasteStepBlocker, wasteSteps, type PendingWasteCapture, type WasteDraft, type WasteStep,
} from '../utils/wasteCapture';
import { formatWasteQuantity } from '../utils/wasteReview';
import { Answers, ChoiceCards, DoneRow, FooterReason, Question } from './capture';
import { ActionBar } from './ui/ActionBar';
import { IngredientPicker, type PickableIngredient } from './capture/IngredientPicker';
import { Modal } from './Modal';
import { useConfirm } from './ui/ConfirmDialog';

// F1 · Registrar merma, para una persona común: «¿Qué se perdió? → ¿Por qué? → ¿Cuánto? → Nota».
// Sin existencias, sin costos, sin fecha (la pone el servidor) y sin restas. La tienda solo se
// pregunta si la persona tiene varias; el servidor valida que sea una de las suyas.

const STEP_TITLES: Record<WasteStep, string> = { STORE: 'Tienda', INGREDIENT: 'Qué se perdió', CATEGORY: 'Por qué', QUANTITY: 'Cuánto', NOTE: 'Nota y revisar' };
const MISSING_INGREDIENT = 'Este insumo ya no está en la lista. Elige otro.';

export function WasteCaptureModal({ options, initial, reviewer, onClose, onSaved, onAttemptStarted, onAttemptFailed }: {
  options: WasteCaptureOptions;
  initial?: PendingWasteCapture | null;
  /** OWNER/MANAGER: la merma cae en SU bandeja; no se le dice «tu gerente la verá». */
  reviewer: boolean;
  onClose: () => void;
  onSaved: (waste: WasteCaptureItem, replayed: boolean) => void;
  /** Justo antes de enviar: la captura se guarda con su llave por si se recarga a media petición. */
  onAttemptStarted: (pending: PendingWasteCapture) => void;
  /**
   * Un envío falló. Si el resultado es DESCONOCIDO (sin conexión, el servidor no respondió o se agotó
   * el tiempo) se entrega la captura para retomarla con la misma llave; si fue un rechazo claro, null.
   */
  onAttemptFailed: (pending: PendingWasteCapture | null) => void;
}) {
  const confirm = useConfirm();
  const askStore = asksWasteStore(options.stores.length);
  const steps = wasteSteps(askStore);
  const storeIds = options.stores.map((store) => store.id);
  const [draft, setDraft] = useState<WasteDraft>(() => initial?.draft ?? emptyWasteDraft(storeIds));
  const [step, setStep] = useState<WasteStep>(() => (initial ? 'NOTE' : steps[0]!));
  // Una llave por INTENTO: si el envío se repite (doble toque, red lenta, reintento o incluso cerrar
  // y retomar una captura en duda), el servidor reconoce la misma merma y no la descuenta dos veces.
  // Solo cambia al empezar una merma nueva.
  const [requestId, setRequestId] = useState(() => initial?.requestId ?? randomUuid());
  const [uncertain, setUncertain] = useState(Boolean(initial));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState<{ waste: WasteCaptureItem; replayed: boolean } | null>(null);

  // El selector compartido solo necesita nombre y unidad: el listado de merma ya viene filtrado
  // (activos y aprobados) y sin costos ni existencias.
  const pickable = useMemo<PickableIngredient[]>(() => options.ingredients.map((ingredient) => ({
    id: ingredient.id, name: ingredient.name, isActive: true, approvalStatus: 'ACTIVE',
    baseUnit: { name: ingredient.unit.name, symbol: ingredient.unit.symbol },
  })), [options.ingredients]);
  const ingredient = options.ingredients.find((option) => option.id === draft.ingredientId);
  const category = options.categories.find((option) => option.code === draft.category);
  const store = options.stores.find((option) => option.id === draft.storeId);
  const requiresNote = wasteCategoryRequiresNote(draft.category, options.categories);
  // En el último paso se revisa TODO (una captura retomada abre directo ahí), no solo la nota.
  const blocker = step === 'NOTE'
    ? steps.map((each) => wasteStepBlocker(each, draft, { requiresNote })).find(Boolean) ?? null
    : wasteStepBlocker(step, draft, { requiresNote });
  const hasContent = Boolean(draft.ingredientId || draft.category || draft.quantity || draft.notes.trim());
  const parsedQuantity = parseWasteQuantity(draft.quantity);
  // Una captura retomada puede apuntar a un insumo que ya no está en la lista: se dice, en vez de un hueco.
  const ingredientLabel = ingredient?.name ?? (draft.ingredientId ? MISSING_INGREDIENT : '');

  const patch = (changes: Partial<WasteDraft>) => { setDraft((current) => ({ ...current, ...changes })); setError(undefined); };

  const save = async () => {
    if (isSaving || blocker) return;
    // Se arma ANTES de enviar: un error local no es un «no sabemos si se guardó».
    let payload: CreateWasteInput;
    try {
      payload = buildWastePayload(draft, { requestId });
    } catch {
      setError('Revisa la cantidad antes de registrar.');
      return;
    }
    setIsSaving(true);
    setError(undefined);
    // Se guarda ANTES de enviar: si la página se recarga con la petición en vuelo, al volver se retoma
    // con la misma llave. Se borra al confirmarse o ante un rechazo definitivo de esta merma.
    onAttemptStarted({ draft, requestId });
    try {
      const result = await recordWaste(payload);
      setSaved({ waste: result.waste, replayed: result.replayed });
      setUncertain(false);
      onSaved(result.waste, result.replayed);
    } catch (saveError) {
      // Solo un rechazo DEFINITIVO de esta merma (validación o regla de negocio) suelta la llave. Sin
      // conexión, 5xx, tiempo agotado, sesión vencida o una respuesta ilegible no prueban nada: la
      // captura se conserva para reintentar sin duplicar.
      const apiError = saveError instanceof ApiError ? saveError : null;
      const code = (apiError?.payload as { error?: string } | undefined)?.error;
      if (apiError && isFinalWasteRejection(apiError.status, code)) {
        setError(getErrorMessage(saveError, 'No fue posible registrar la merma.'));
        onAttemptFailed(null);
      } else {
        setUncertain(true);
        setError(apiError && apiError.status >= 400 && apiError.status < 500
          ? `${getErrorMessage(saveError, 'No fue posible registrar la merma.')} La merma queda guardada para reintentar; si ya se había registrado, no se duplica.`
          : 'No sabemos si se guardó: se perdió la conexión o el servidor no respondió. Toca «Registrar merma» otra vez; si ya se había guardado, no se duplica.');
        onAttemptFailed({ draft, requestId });
      }
    } finally {
      setIsSaving(false);
    }
  };
  const startAnother = () => {
    setDraft({ ...emptyWasteDraft(storeIds), storeId: draft.storeId });
    setRequestId(randomUuid());
    setSaved(null);
    setUncertain(false);
    setError(undefined);
    setStep(askStore ? 'INGREDIENT' : steps[0]!);
  };
  const cancel = async () => {
    if (isSaving) return;
    // Una captura en duda NO se descarta aquí: la pantalla la guarda para retomarla con la misma llave
    // (y ahí mismo se puede descartar a propósito).
    if (!saved && !uncertain && hasContent && !(await confirm({ title: '¿Descartar la merma?', description: 'Lo capturado hasta ahora se perderá.', confirmLabel: 'Descartar', cancelLabel: 'Seguir capturando', danger: true }))) return;
    onClose();
  };
  const goNext = () => {
    if (blocker) return;
    if (step === 'NOTE') { void save(); return; }
    setStep(nextWasteStep(step, steps));
  };

  const unitLabel = ingredient ? `${ingredient.unit.name} (${ingredient.unit.symbol})` : '';
  let body: JSX.Element;
  if (saved) {
    const { waste, replayed } = saved;
    const what = `${formatWasteQuantity(waste.quantity, waste.unitSymbol)} de ${waste.ingredientName}`;
    const next = reviewer ? 'Ya se descontó del inventario y aparece en «Mermas de hoy».' : 'Ya se descontó del inventario y tu gerente la verá en su revisión.';
    // Reintento de una captura que ya se había guardado y que alguien anuló después: no se dice «ya
    // se descontó», porque se devolvió al anularla.
    body = waste.reversed
      ? <div className="alert alert--warning" role="status">
        <strong>Esta merma ya se había registrado y después se anuló</strong> ({what}){waste.reversalReason ? `: ${waste.reversalReason}` : ''}. No se volvió a descontar. Si de verdad se perdió, toca «Registrar otra».
      </div>
      : <div className="alert alert--success" role="status">
        <strong>Listo.</strong> {replayed ? `Esta merma ya estaba registrada y no se duplicó: ${what}` : `Registraste ${what}`} ({category?.label ?? 'merma'}). {next}
      </div>;
  } else {
    switch (step) {
      case 'STORE':
        body = <>
          <Question>¿En qué tienda pasó?</Question>
          <ChoiceCards ariaLabel="Tienda" onChange={(value) => patch({ storeId: value })} options={options.stores.map((option) => ({ value: option.id, label: option.name }))} stack value={draft.storeId} />
        </>;
        break;
      case 'INGREDIENT':
        body = options.ingredients.length === 0
          ? <><Question>¿Qué se perdió?</Question><Answers><p className="wiz-hint">{reviewer
            // P-078 (BK26): al dueño o gerente no se le manda con «tu dueño o gerente»: es quien da de alta.
            ? <>No hay insumos activos para elegir. Dalos de alta en <a className="link-button" href="#inventory/ingredients">Inventario › Insumos</a> o aprueba los propuestos en <a className="link-button" href="#catalog-approvals">Aprobaciones de catálogo</a>.</>
            : 'No hay insumos para elegir. Pide a tu dueño o gerente que los dé de alta.'}</p></Answers></>
          : <IngredientPicker ingredients={pickable} onChange={(ingredientId) => patch({ ingredientId })} question="¿Qué se perdió?" value={draft.ingredientId} />;
        break;
      case 'CATEGORY':
        body = <>
          <DoneRow label="Qué se perdió" onChange={() => setStep('INGREDIENT')} value={ingredientLabel} />
          <Question>¿Por qué se perdió?</Question>
          <ChoiceCards ariaLabel="Por qué se perdió" onChange={(value) => patch({ category: value })} options={options.categories.map((option) => ({ value: option.code, label: option.label, hint: option.requiresNote ? 'Tendrás que contar qué pasó' : undefined }))} value={draft.category} />
        </>;
        break;
      case 'QUANTITY':
        body = <>
          <DoneRow label="Qué se perdió" onChange={() => setStep('INGREDIENT')} value={ingredientLabel} />
          <DoneRow label="Por qué" onChange={() => setStep('CATEGORY')} value={category?.label ?? ''} />
          <Question>¿Cuánto se perdió?</Question>
          <Answers>
            <div className="pos-stepper waste-stepper" role="group" aria-label="Cantidad">
              <button aria-label="Quitar uno" className="button button--ghost" disabled={isSaving} onClick={() => patch({ quantity: stepWasteQuantity(draft.quantity, -1) })} type="button">−</button>
              <label className="field wiz-big-input waste-stepper__field">
                <span>Cantidad{unitLabel ? ` en ${unitLabel}` : ''}</span>
                <input autoFocus enterKeyHint="done" inputMode="decimal" maxLength={14} onChange={(event) => patch({ quantity: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); } }} placeholder="0" type="text" value={draft.quantity} />
              </label>
              <button aria-label="Agregar uno" className="button button--primary" disabled={isSaving} onClick={() => patch({ quantity: stepWasteQuantity(draft.quantity, 1) })} type="button">+</button>
            </div>
            <p className="wiz-hint">Escribe la cantidad o usa − y + para piezas sueltas.</p>
          </Answers>
        </>;
        break;
      case 'NOTE':
      default:
        body = <>
          <Question>{requiresNote ? '¿Qué pasó?' : 'Nota (opcional)'}</Question>
          <Answers>
            <label className="field">
              <span>{requiresNote ? 'Cuéntanos qué pasó (obligatorio)' : reviewer ? 'Algo que quieras dejar anotado' : 'Algo que tu gerente deba saber'}</span>
              <textarea maxLength={500} onChange={(event) => patch({ notes: event.target.value })} placeholder={requiresNote ? 'Ej. Se descompuso el congelador' : 'Opcional'} rows={2} value={draft.notes} />
            </label>
          </Answers>
          <Question>Revisa antes de registrar</Question>
          {askStore && <DoneRow label="Tienda" onChange={() => setStep('STORE')} value={store?.name ?? ''} />}
          <DoneRow label="Qué se perdió" onChange={() => setStep('INGREDIENT')} value={ingredientLabel} />
          <DoneRow label="Por qué" onChange={() => setStep('CATEGORY')} value={category?.label ?? ''} />
          {/* La cantidad YA interpretada, no el texto tecleado: así «1,000» nunca pasa por 1. */}
          <DoneRow label="Cuánto" onChange={() => setStep('QUANTITY')} value={parsedQuantity === null ? 'Revisa la cantidad' : formatWasteQuantity(String(parsedQuantity), ingredient?.unit.symbol ?? '').trim()} />
          <p className="wiz-hint">Se registra con la hora de ahora y se descuenta del inventario al momento.</p>
        </>;
        break;
    }
  }

  return (
    <Modal
      title="Registrar merma"
      description={saved ? (saved.waste.reversed ? 'Merma ya anulada' : 'Merma registrada') : `Paso ${steps.indexOf(step) + 1} de ${steps.length} · ${STEP_TITLES[step]}`}
      isBusy={isSaving}
      onClose={() => void cancel()}
      // P-078 (ActionBar v2 · G): el mismo pie (nota en su renglón, Cancelar · Atrás y la primaria), con la pieza común
      // para que se vea igual que el resto de las hojas; ninguna condición cambia.
      footer={saved
        ? <ActionBar aria-label="Acciones de la merma" primary={<button className="button button--primary" onClick={onClose} type="button">Listo</button>}>
          <button className="button button--ghost" onClick={startAnother} type="button">Registrar otra</button>
        </ActionBar>
        : <ActionBar aria-label="Acciones de la merma" note={blocker ? <FooterReason>{blocker}</FooterReason> : undefined} primary={<button className="button button--primary" disabled={isSaving || Boolean(blocker)} onClick={goNext} type="button">{step === 'NOTE' ? (isSaving ? 'Registrando…' : 'Registrar merma') : 'Siguiente'}</button>}>
          <button className="text-button" disabled={isSaving} onClick={() => void cancel()} type="button">{uncertain ? 'Cerrar' : 'Cancelar'}</button>
          {step !== steps[0] && <button className="button button--ghost" disabled={isSaving} onClick={() => setStep(previousWasteStep(step, steps))} type="button">Atrás</button>}
        </ActionBar>}
    >
      {initial && uncertain && !error && <div className="alert alert--warning" role="status">Retomas una merma cuyo registro no se confirmó. Revísala y toca «Registrar merma»: si ya se había guardado, no se duplica.</div>}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {body}
    </Modal>
  );
}
